import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const TermsOfService = () => {
  useEffect(() => {
    document.title = 'Terms of Service - Manica Solutions';
  }, []);

  return (
    <div className="bg-background min-h-screen">
      <Helmet>
        <meta name="description" content="Terms of Service for Manica Solutions - Read our terms and conditions for using our services." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand/10 via-secondary/5 to-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gradient-brand mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Please read these terms carefully before using our services. By using our services, you agree to these terms.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} />
                <span>Last updated: {new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} />
                <span>Legal Document</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Agreement */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Agreement to Terms</h2>
              <p className="text-text-secondary mb-4">
                These Terms of Service ("Terms") govern your use of the services provided by Manica Solutions ("we," "our," or "us"), including our website, software development services, consulting services, and any other services we provide.
              </p>
              <p className="text-text-secondary">
                By accessing or using our services, you agree to be bound by these Terms. If you disagree with any part of these terms, you may not access our services.
              </p>
            </div>

            {/* Services Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Description of Services</h2>
              <p className="text-text-secondary mb-4">
                Manica Solutions provides the following services:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Custom software development and programming</li>
                <li>Digital transformation consulting</li>
                <li>MVP (Minimum Viable Product) development</li>
                <li>Enterprise system integration</li>
                <li>Web and mobile application development</li>
                <li>Technical consulting and advisory services</li>
                <li>Project management and delivery</li>
              </ul>
            </div>

            {/* User Responsibilities */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">User Responsibilities</h2>
              <p className="text-text-secondary mb-4">
                When using our services, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account and credentials</li>
                <li>Use our services only for lawful purposes</li>
                <li>Not interfere with or disrupt our services</li>
                <li>Not attempt to gain unauthorized access to our systems</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect intellectual property rights</li>
              </ul>
            </div>

            {/* Payment Terms */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Payment Terms</h2>
              <p className="text-text-secondary mb-4">
                Payment terms for our services are as follows:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Payment schedules will be agreed upon in individual service agreements</li>
                <li>All fees are due within the specified payment terms</li>
                <li>Late payments may result in suspension of services</li>
                <li>All fees are non-refundable unless otherwise specified</li>
                <li>We reserve the right to modify pricing with appropriate notice</li>
                <li>Taxes will be added where applicable</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-text-primary mb-3">Our Intellectual Property</h3>
              <p className="text-text-secondary mb-4">
                All content, trademarks, service marks, trade names, logos, and intellectual property on our website and in our services are owned by Manica Solutions or our licensors.
              </p>

              <h3 className="text-xl font-semibold text-text-primary mb-3">Client Intellectual Property</h3>
              <p className="text-text-secondary mb-4">
                For custom development projects, intellectual property rights will be specified in individual project agreements. Generally:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Custom code developed for clients becomes their property upon full payment</li>
                <li>Third-party libraries and frameworks remain under their respective licenses</li>
                <li>Our proprietary frameworks and tools remain our property</li>
                <li>Client data and content remain the property of the client</li>
              </ul>
            </div>

            {/* Confidentiality */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Confidentiality</h2>
              <p className="text-text-secondary mb-4">
                We are committed to maintaining the confidentiality of your information:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>We will not disclose confidential information to third parties</li>
                <li>We implement appropriate security measures to protect your data</li>
                <li>Our employees are bound by confidentiality agreements</li>
                <li>We may disclose information if required by law</li>
                <li>Confidentiality obligations survive termination of services</li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Limitation of Liability</h2>
              <p className="text-text-secondary mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Our liability is limited to the amount paid for the specific service</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>We are not liable for data loss or system failures</li>
                <li>We are not liable for third-party actions or content</li>
                <li>These limitations apply even if we have been advised of potential damages</li>
              </ul>
            </div>

            {/* Warranty Disclaimer */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Warranty Disclaimer</h2>
              <p className="text-text-secondary mb-4">
                Our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that services will be uninterrupted or error-free</li>
                <li>Warranties regarding the accuracy or completeness of information</li>
                <li>Warranties that defects will be corrected</li>
              </ul>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Termination</h2>
              <p className="text-text-secondary mb-4">
                Either party may terminate services with written notice:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>We may terminate for breach of these terms</li>
                <li>You may terminate at any time with appropriate notice</li>
                <li>Upon termination, you must cease using our services</li>
                <li>Certain provisions survive termination (confidentiality, IP, etc.)</li>
                <li>Outstanding payments remain due upon termination</li>
              </ul>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Governing Law and Dispute Resolution</h2>
              <p className="text-text-secondary mb-4">
                These Terms are governed by the laws of South Africa. Any disputes will be resolved through:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Good faith negotiations between the parties</li>
                <li>Mediation if negotiations fail</li>
                <li>Arbitration or court proceedings as a last resort</li>
                <li>Venue for legal proceedings will be Johannesburg, South Africa</li>
              </ul>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Severability</h2>
              <p className="text-text-secondary mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Changes to Terms</h2>
              <p className="text-text-secondary mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of any changes by:
              </p>
              <ul className="list-disc pl-6 mb-6 text-text-secondary space-y-2">
                <li>Posting the new Terms on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Updating the "Last updated" date</li>
              </ul>
              <p className="text-text-secondary">
                Your continued use of our services after changes become effective constitutes acceptance of the new Terms.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Contact Information</h2>
              <p className="text-text-secondary mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-muted p-6 rounded-brand">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="Mail" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Email: legal@manicasolutions.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Phone" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Phone: +27110836344</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={20} className="text-secondary" />
                    <span className="text-text-secondary">Address: Northgate, Randburg, Johannesburg, South Africa</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
